/**
 * Drawing Tool Types
 *
 * Full TradingView-style drawing tool system.
 * Drawings are rendered as an SVG overlay on top of the lightweight-charts canvas.
 *
 * Tool categories (matches TradingView left sidebar):
 *   Pointer   — Cursor
 *   Lines     — Trend Line, Ray, Extended Line, Horizontal Ray
 *   H/V Lines — Horizontal Line, Vertical Line
 *   Channel   — Parallel Channel
 *   Fibonacci — Fib Retracement, Fib Extension
 *   Shapes    — Rectangle, Ellipse, Triangle
 *   Annotate  — Text, Arrow, Price Label
 *   Measure   — Measure / Price Range
 */

// ── Drawing tool identifiers ──────────────────────────────────────
export type DrawingTool =
  | "cursor"
  | "trendline" | "ray" | "extended-line" | "horizontal-ray"
  | "horizontal" | "vertical"
  | "parallel-channel"
  | "fibonacci" | "fib-extension" | "fib-fan" | "fib-timezone" | "fib-arc"
  | "gann-fan" | "pitchfork"
  | "rectangle" | "ellipse" | "triangle"
  | "text" | "arrow" | "price-label"
  | "measure";

// ── Shared types ──────────────────────────────────────────────────
export interface ChartPoint {
  time: number;  // millisecond timestamp (UTC)
  price: number;
}

export interface DrawingBase {
  id: string;
  tool: DrawingTool;
  color: string;
  lineWidth: number;
  visible: boolean;
}

// ── 1-click tools ─────────────────────────────────────────────────

/** Horizontal price line — full-width at a price */
export interface HorizontalLineDrawing extends DrawingBase {
  tool: "horizontal";
  price: number;
}

/** Vertical time line — full-height at a timestamp */
export interface VerticalLineDrawing extends DrawingBase {
  tool: "vertical";
  time: number;
}

/** Horizontal ray — from a point extending right */
export interface HorizontalRayDrawing extends DrawingBase {
  tool: "horizontal-ray";
  point: ChartPoint;
}

/** Price label — tag at a specific (time, price) */
export interface PriceLabelDrawing extends DrawingBase {
  tool: "price-label";
  point: ChartPoint;
}

/** Text annotation */
export interface TextDrawing extends DrawingBase {
  tool: "text";
  point: ChartPoint;
  text: string;
  fontSize: number;
}

// ── 2-click tools ─────────────────────────────────────────────────

/** Trend line — two anchor points */
export interface TrendlineDrawing extends DrawingBase {
  tool: "trendline";
  point1: ChartPoint;
  point2: ChartPoint;
  ray?: boolean;
}

/** Ray — from point1, extending infinitely through point2 */
export interface RayDrawing extends DrawingBase {
  tool: "ray";
  point1: ChartPoint;
  point2: ChartPoint;
}

/** Extended line — through both points, extending infinitely both directions */
export interface ExtendedLineDrawing extends DrawingBase {
  tool: "extended-line";
  point1: ChartPoint;
  point2: ChartPoint;
}

/** Fibonacci retracement — high/low with level lines */
export interface FibonacciDrawing extends DrawingBase {
  tool: "fibonacci";
  point1: ChartPoint;
  point2: ChartPoint;
  levels: number[];
}

/** Rectangle — two corner points */
export interface RectangleDrawing extends DrawingBase {
  tool: "rectangle";
  point1: ChartPoint;
  point2: ChartPoint;
  fillColor: string;
  fillOpacity: number;
}

/** Ellipse — bounding-box corners */
export interface EllipseDrawing extends DrawingBase {
  tool: "ellipse";
  point1: ChartPoint;
  point2: ChartPoint;
  fillColor: string;
  fillOpacity: number;
}

/** Arrow — line with arrowhead from point1 to point2 */
export interface ArrowDrawing extends DrawingBase {
  tool: "arrow";
  point1: ChartPoint;
  point2: ChartPoint;
}

/** Measure — shows price diff, % change, bar count */
export interface MeasureDrawing extends DrawingBase {
  tool: "measure";
  point1: ChartPoint;
  point2: ChartPoint;
}

// ── 3-click tools ─────────────────────────────────────────────────

/** Parallel channel — two points define main line, third defines width */
export interface ParallelChannelDrawing extends DrawingBase {
  tool: "parallel-channel";
  point1: ChartPoint;
  point2: ChartPoint;
  point3: ChartPoint;
  fillColor: string;
  fillOpacity: number;
}

/** Fibonacci extension — 3 points (A→B retracement, C projection) */
export interface FibExtensionDrawing extends DrawingBase {
  tool: "fib-extension";
  point1: ChartPoint;
  point2: ChartPoint;
  point3: ChartPoint;
  levels: number[];
}

/** Triangle — 3 vertices */
export interface TriangleDrawing extends DrawingBase {
  tool: "triangle";
  point1: ChartPoint;
  point2: ChartPoint;
  point3: ChartPoint;
  fillColor: string;
  fillOpacity: number;
}

/** Fibonacci Time Zones — vertical lines at Fibonacci time intervals */
export interface FibTimezoneDrawing extends DrawingBase {
  tool: "fib-timezone";
  point1: ChartPoint;
  point2: ChartPoint;
}

/** Fibonacci Fan — diagonal lines from pivot at Fibonacci angles */
export interface FibFanDrawing extends DrawingBase {
  tool: "fib-fan";
  point1: ChartPoint;
  point2: ChartPoint;
}

/** Fibonacci Arc — curved arcs at Fibonacci price levels */
export interface FibArcDrawing extends DrawingBase {
  tool: "fib-arc";
  point1: ChartPoint;
  point2: ChartPoint;
}

/** Gann Fan — diagonal lines at Gann angles (1x8, 1x4, 1x3, 1x2, 1x1, 2x1, 3x1, 4x1, 8x1) */
export interface GannFanDrawing extends DrawingBase {
  tool: "gann-fan";
  point1: ChartPoint;
  point2: ChartPoint;
}

/** Andrews' Pitchfork — three parallel trendlines from pivot points */
export interface PitchforkDrawing extends DrawingBase {
  tool: "pitchfork";
  point1: ChartPoint;
  point2: ChartPoint;
  point3: ChartPoint;
}

// ── Union type ────────────────────────────────────────────────────
export type Drawing =
  | HorizontalLineDrawing
  | VerticalLineDrawing
  | HorizontalRayDrawing
  | PriceLabelDrawing
  | TextDrawing
  | TrendlineDrawing
  | RayDrawing
  | ExtendedLineDrawing
  | FibonacciDrawing
  | FibExtensionDrawing
  | FibFanDrawing
  | FibTimezoneDrawing
  | FibArcDrawing
  | GannFanDrawing
  | PitchforkDrawing
  | RectangleDrawing
  | EllipseDrawing
  | ArrowDrawing
  | MeasureDrawing
  | ParallelChannelDrawing
  | TriangleDrawing;

// ── Constants ─────────────────────────────────────────────────────
export const DEFAULT_FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
export const DEFAULT_FIB_EXT_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 1, 1.272, 1.618, 2.618];

export const DRAWING_COLORS = [
  "#2196F3", // blue
  "#FF5722", // red-orange
  "#4CAF50", // green
  "#FF9800", // orange
  "#9C27B0", // purple
  "#00BCD4", // cyan
  "#FFEB3B", // yellow
  "#E91E63", // pink
];

/** Number of clicks required to complete a drawing */
export function getRequiredClicks(tool: DrawingTool): number {
  switch (tool) {
    case "horizontal":
    case "vertical":
    case "horizontal-ray":
    case "price-label":
    case "text":
      return 1;
    case "trendline":
    case "ray":
    case "extended-line":
    case "rectangle":
    case "ellipse":
    case "arrow":
    case "measure":
    case "fibonacci":
    case "fib-fan":
    case "fib-timezone":
    case "fib-arc":
    case "gann-fan":
      return 2;
    case "parallel-channel":
    case "fib-extension":
    case "triangle":
    case "pitchfork":
      return 3;
    default:
      return 0;
  }
}

/** Default line width for each tool */
export function getDefaultLineWidth(tool: DrawingTool): number {
  switch (tool) {
    case "trendline":
    case "ray":
    case "extended-line":
    case "arrow":
      return 2;
    case "horizontal":
    case "vertical":
    case "horizontal-ray":
    case "fibonacci":
    case "fib-extension":
    case "fib-fan":
    case "fib-timezone":
    case "fib-arc":
    case "gann-fan":
    case "pitchfork":
    case "measure":
      return 1;
    case "parallel-channel":
    case "rectangle":
    case "ellipse":
    case "triangle":
      return 1.5;
    default:
      return 1;
  }
}
